import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../../context/NotificationContext';
import { contentAPI } from '../../../utils/api';
import { formatImageUrl } from '../../../utils/imageUtils';
import { HomeSkeleton } from '../../../components/SkeletonLoaders';

const VideoModal = ({ video, onClose }) => {
    if (!video) return null;

    const getEmbedUrl = (url) => {
        if (!url) return '';
        if (url.includes('youtube.com/watch?v=')) {
            const id = url.split('v=')[1]?.split('&')[0];
            return `https://www.youtube.com/embed/${id}?autoplay=1`;
        }
        if (url.includes('youtu.be/')) {
            const id = url.split('youtu.be/')[1]?.split('?')[0];
            return `https://www.youtube.com/embed/${id}?autoplay=1`;
        }
        return url;
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 animate-fade-in">
            <div className="relative w-full max-w-4xl bg-gray-900 rounded-2xl overflow-hidden shadow-2xl border border-gold/30">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/80 text-white p-2 rounded-full transition-colors"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <div className="aspect-video w-full bg-black">
                    <iframe
                        src={getEmbedUrl(video.video_url)}
                        title={video.title}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    ></iframe>
                </div>

                <div className="p-6">
                    <h2 className="text-xl font-bold text-gold mb-2">{video.title}</h2>
                    <p className="text-gray-400 text-sm">{video.description}</p>
                </div>
            </div>
        </div>
    );
};

const PatientPodcasts = () => {
    const navigate = useNavigate();
    const { showError } = useNotification();
    const [podcasts, setPodcasts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPodcast, setSelectedPodcast] = useState(null);

    useEffect(() => {
        fetchPodcasts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchPodcasts = async () => {
        try {
            const res = await contentAPI.getPodcasts();
            if (res.data.success) {
                setPodcasts(res.data.data || []);
            }
        } catch (error) {
            showError('Failed to load podcasts');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <HomeSkeleton />;
    }

    return (
        <div className="space-y-6 pb-20 pt-4">
            {selectedPodcast && (
                <VideoModal video={selectedPodcast} onClose={() => setSelectedPodcast(null)} />
            )}

            <div className="flex items-center space-x-3 mb-6">
                <button onClick={() => navigate(-1)} className="text-gold p-2 hover:bg-gray-800 rounded-lg transition-colors">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <h1 className="text-2xl font-bold text-gold">Expert Podcasts</h1>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {podcasts.length === 0 ? (
                    <div className="col-span-1 md:col-span-2 text-center py-12 text-gray-400 bg-gray-800 rounded-2xl">
                        <p>No podcasts available right now.</p>
                    </div>
                ) : (
                    podcasts.map((podcast) => (
                        <div
                            key={podcast._id}
                            onClick={() => setSelectedPodcast(podcast)}
                            className="bg-gray-800 rounded-xl overflow-hidden block cursor-pointer group shadow-lg hover:border-gold/30 transition-all"
                        >
                            <div className="aspect-video bg-gray-700 flex items-center justify-center relative">
                                {podcast.thumbnail ? (
                                    <img
                                        src={formatImageUrl(podcast.thumbnail)}
                                        alt={podcast.title}
                                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                                    />
                                ) : (
                                    <div className="bg-gray-700 w-full h-full flex items-center justify-center">
                                        <span className="text-gray-500">No Preview</span>
                                    </div>
                                )}
                                <div className="absolute bg-gold/80 text-dark rounded-full p-3 group-hover:scale-110 transition-transform">
                                    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                            <div className="p-5">
                                <h3 className="font-bold text-lg line-clamp-2 text-gold mb-2">{podcast.title}</h3>
                                <p className="text-gray-400 text-sm mt-1">{podcast.category || 'Podcast'}</p>
                                <p className="text-gray-500 text-xs mt-2 line-clamp-2">{podcast.description}</p>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default PatientPodcasts;
